// PlanosClienteAssinaturasList.tsx
import React, { useEffect, useState } from 'react';
import AssinaturaCard from './AssinaturaCard';
import { api } from '../../../AxiosConfig';
import { Modal } from '../../../components/Modal';
import { useAppContext } from '../../../contexts/AppContext';
import type { PlanPeriodicity, SingleValidityUnit } from './planoTypes';
import { IdCard, TriangleAlert } from 'lucide-react';

interface AssinaturaPeriodoServico {
    serviceId: string;
    serviceName?: string;
    limit: number;
    used: number;
}

interface AssinaturaClientePeriodoResumo {
    id: string;
    inicio: string;
    fim: string;
    status: string;
    cobrancaStatus: string;
    valorPrevisto: number;
    valorPago: number;
    dueAt?: string;
    paidAt?: string;
    servicos: AssinaturaPeriodoServico[];
}

interface AssinaturaClientePlano {
    id: string;
    name: string;
    description?: string;
    price: number;
    periodicity?: PlanPeriodicity | null;
    quantity?: number;
    indeterminateQuantity?: boolean;
    singleValidityValue?: number;
    singleValidityUnit?: SingleValidityUnit;
    services: Array<{
        serviceId: string;
        recurrence?: string;
        quantity: number;
    }>;
}

interface AssinaturaClienteFinanceiro {
    paidPeriodsCount: number;
    remainingPeriodsCount: number;
    totalPaidAmount: number;
    totalRemainingAmount: number;
    lastPaymentAt?: string;
    lastPaymentAmount?: number;
    lastBillingStatus?: string;
}

export interface AssinaturaClienteView {
    id: string;
    estabId: string;
    clienteId: string;
    status: number; // 0 = cancelada, 1 = ativa
    billingType: string;
    startAt: string;
    nextBillingAt?: string;
    endAt?: string;
    plano: AssinaturaClientePlano;
    financeiro: AssinaturaClienteFinanceiro;
    periodoAtual?: AssinaturaClientePeriodoResumo;
}

// 👇 AQUI está o tipo correto das props
type AssinaturasListProps = {
    estabId: string;
};

const PlanosClienteAssinaturasList: React.FC<AssinaturasListProps> = ({
                                                                          estabId,
                                                                      }) => {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [assinaturas, setAssinaturas] = useState<AssinaturaClienteView[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [assinaturaToCancel, setAssinaturaToCancel] = useState<AssinaturaClienteView | null>(null);
    const [canceling, setCanceling] = useState(false);

    const loadAssinaturas = async (currentEstabId: string) => {
        try {
            setLoading(true);
            setError(false);

            const { data } = await api.get<AssinaturaClienteView[]>(
                '/assinaturas/cliente',
                {
                    params: {
                        estabId: currentEstabId,
                    },
                    headers: {
                        'x-estab-id': currentEstabId,
                    },
                },
            );

            setAssinaturas(data || []);
        } catch (e) {
            console.error(e);
            setError(true);
            setAssinaturas([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!estabId) {
            setAssinaturas([]);
            return;
        }
        loadAssinaturas(estabId);
    }, [estabId]);

    const handleCancel = async () => {
        if (!assinaturaToCancel) return;

        try {
            setCanceling(true);

            await api.post(
                '/assinaturas/cliente/cancel',
                { _id: assinaturaToCancel.id, estabId },
                {
                    headers: {
                        'x-estab-id': estabId,
                    },
                },
            );
            setAssinaturaToCancel(null);
            successSnackbar('Assinatura cancelada.');
            await loadAssinaturas(estabId);
        } catch (e) {
            console.error(e);
            errorSnackbar('Não foi possível cancelar a assinatura. Tente novamente.');
        } finally {
            setCanceling(false);
        }
    };

    if (loading) {
        return (
            <div className="pla-grid" aria-busy="true">
                {[0, 1].map((item) => (
                    <div key={item} className="sf-skeleton h-72" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar suas assinaturas</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => loadAssinaturas(estabId)}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!assinaturas.length) {
        return (
            <div className="sf-state">
                <IdCard aria-hidden />
                <h2>Nenhuma assinatura</h2>
                <p>Os planos que você contratar neste estabelecimento aparecem aqui.</p>
            </div>
        );
    }

    // Assinaturas ativas primeiro; a ordem da API e preservada dentro de cada grupo.
    const ordered = [...assinaturas].sort((a, b) => Number(b.status === 1) - Number(a.status === 1));

    return (
        <>
            <div className="pla-grid">
                {ordered.map((ass) => (
                    <AssinaturaCard key={ass.id} assinatura={ass} onCancel={setAssinaturaToCancel} />
                ))}
            </div>

            {assinaturaToCancel && (
                <Modal
                    title="Cancelar assinatura?"
                    onClose={() => setAssinaturaToCancel(null)}
                    desktopClassName="sm:!max-w-md"
                >
                    <p className="text-sm text-base-content/70">
                        A assinatura de {assinaturaToCancel.plano.name} será cancelada, e os agendamentos gerados por ela
                        que ainda estão em aberto também. Esta ação não pode ser desfeita.
                    </p>
                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setAssinaturaToCancel(null)}
                            disabled={canceling}
                        >
                            Manter assinatura
                        </button>
                        <button type="button" className="btn btn-error" onClick={handleCancel} disabled={canceling}>
                            {canceling && <span className="loading loading-spinner loading-sm"></span>}
                            Cancelar assinatura
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default PlanosClienteAssinaturasList;
